/**
 * Permissions store — local zustand-backed mirror of the legacy Redux
 * `permissions` slice from clario-frontend. Hooks in this package read
 * from here.
 *
 * Wave 2 ports the consumer hooks; Wave 3 (services) will wire the API
 * matrix loader that hydrates this store. Until then `loaded` is `false`
 * and every check default-denies (matches the phase-0 shell stub).
 */

import { create } from 'zustand';
import type { PermissionKey } from './permission-keys';

/** Source of a permission (where it came from after merging base + project). */
export type PermissionSource = 'base' | 'project' | 'override';

export interface ProjectPermCache {
  permissions: Record<string, boolean>;
  sources: Record<string, PermissionSource>;
}

export interface PermissionsState {
  /** Whether the permission matrix has been loaded at least once. */
  loaded: boolean;
  /** Base (org-level) permissions as returned by the matrix endpoint. */
  basePermissions: Record<string, boolean>;
  baseSources: Record<string, PermissionSource>;
  /** Per-project permission caches keyed by project id. */
  projectPermissions: Record<string, ProjectPermCache>;
  /** Effective merged permissions (base merged with the active project's overrides). */
  permissions: Record<string, boolean>;
  /** Source attribution for the merged set. */
  sources: Record<string, PermissionSource>;
  /** Active project id when a project context is selected; null at org level. */
  activeProjectId: string | null;
  /**
   * Per-app hydration flags, keyed by app slug (e.g. `payroll`), flipped by
   * `publishAppPermissions()`. The shell gates an app's sidebar panel rows on ITS
   * flag (the rail fetches its own map); `loaded` is not implied by these.
   */
  loadedApps: Record<string, boolean>;
  /**
   * `workspace.AppModule` codes this workspace is NOT entitled to — the
   * per-tenant module layer a Sarvadhi platform admin controls.
   *
   * Distinct from permissions and NOT a substitute for them: permissions answer
   * "may this user do it?", this answers "did this customer get it at all?".
   * A DENY-list, so an empty set (the initial state, and any backend that
   * predates the field) hides nothing — the backend denies these routes
   * regardless, so this only spares the user a dead-end click.
   */
  disabledModules: string[];
  /**
   * Permission keys the backend still SHOWS for this workspace — the union of
   * every key in the matrix endpoint's `modules` payload, which the server has
   * already filtered by module entitlement.
   *
   * `null` means "not known yet" (store not hydrated, or an older backend that
   * sends no `modules`): consumers must then filter NOTHING. Fail-open, matching
   * every other layer of this feature.
   *
   * This exists because the RBAC matrix tree is a HARDCODED client-side
   * structure (`RBAC_MODULES`) whose group ids do not match the backend's
   * catalog modules — so it cannot be filtered by `disabledModules` without a
   * mapping table that would drift. Filtering by the server's own surviving key
   * set needs no mapping and cannot disagree with the API.
   */
  entitledKeys: ReadonlySet<string> | null;
  /**
   * Platform feature flags an app read for this workspace, by code (`HRMS_LEAVE_TEAM_PAGES`), fed by
   * `publishFeatureFlags()`. Panel rows with a `flag` predicate read it; a code not here is off.
   */
  featureFlags: Record<string, boolean>;
}

const initialState: PermissionsState = {
  loaded: false,
  basePermissions: {},
  baseSources: {},
  projectPermissions: {},
  permissions: {},
  sources: {},
  activeProjectId: null,
  loadedApps: {},
  disabledModules: [],
  featureFlags: {},
  entitledKeys: null,
};

const GLOBAL_KEY = '__clarixRbacPermissionsStore__';
type GlobalWithStore = typeof globalThis & {
  [GLOBAL_KEY]?: ReturnType<typeof makeStore>;
};

function makeStore() {
  return create<PermissionsState>()(() => initialState);
}

const g = globalThis as GlobalWithStore;
export const usePermissionsStore = g[GLOBAL_KEY] ?? (g[GLOBAL_KEY] = makeStore());

/** Imperative read of the current state — mirrors `store.getState().permissions`. */
export function getPermissionsState(): PermissionsState {
  return usePermissionsStore.getState();
}

/** Replace the entire state — used by service-layer hydrators (Wave 3). */
export function setPermissionsState(next: Partial<PermissionsState>): void {
  usePermissionsStore.setState(next);
}

/**
 * Merge one app's shell-namespace permission slugs (e.g. `payroll.users.view`)
 * into the effective set and mark that app hydrated, on every fetch or live refresh.
 * Feeds the shell's panel links and the rail's refetch-on-divergence check. Revoked
 * slugs must be passed as explicit `false` — merging never deletes.
 */
export function publishAppPermissions(
  appSlug: string,
  perms: Record<string, boolean>,
): void {
  usePermissionsStore.setState((s) => ({
    permissions: { ...s.permissions, ...perms },
    loadedApps: { ...s.loadedApps, [appSlug]: true },
  }));
}

/** Merge an app's feature flags (explicit booleans, so a flag switched off stays off) for the shell's panel rows. */
export function publishFeatureFlags(flags: Readonly<Record<string, boolean>>): void {
  usePermissionsStore.setState((s) => ({ featureFlags: { ...s.featureFlags, ...flags } }));
}

/** Drop an app's hydration flag (logout, or a failed permission fetch) for its panel rows. */
export function clearAppPermissions(appSlug: string): void {
  usePermissionsStore.setState((s) => ({
    loadedApps: { ...s.loadedApps, [appSlug]: false },
  }));
}

/** Convenience selector hook — returns the full state. */
export function usePermissionsSlice(): PermissionsState {
  return usePermissionsStore();
}

/** Type guard helper to read a single permission boolean. */
export function hasPermissionKey(state: PermissionsState, key: PermissionKey): boolean {
  return state.permissions[key] === true;
}

/**
 * Shape returned by the backend `/me/permissions` (or equivalent) endpoint.
 *
 * `base` is the org-level matrix; `projects` is a per-project override map.
 * `sources` annotates each key with where it came from so the UI can show
 * "inherited from base" vs. "project override" badges.
 */
export interface PermissionMatrixPayload {
  base: Record<string, boolean>;
  baseSources?: Record<string, PermissionSource>;
  projects?: Record<string, ProjectPermCache>;
  /**
   * Optional active project id. When provided, the merged `permissions` map
   * is computed as `{...base, ...projects[activeProjectId].permissions}`.
   * Pass `null` to drop back to org-level only.
   */
  activeProjectId?: string | null;
  /** AppModule codes the workspace lacks (`disabled_modules` from the matrix endpoint). */
  disabledModules?: string[];
  /** Every key present in the matrix endpoint's (already filtered) `modules` payload. */
  entitledKeys?: Iterable<string> | null;
}

/**
 * Hydrate the permissions store from a backend matrix response.
 *
 * --------------------------------------------------------------------------
 * CONTRACT
 * --------------------------------------------------------------------------
 *  - Consuming apps should call this once after login (or any time the user
 *    /workspace context changes) with the response from the matrix endpoint.
 *  - This is a full replace, not a patch — pass the complete current matrix.
 *  - Sets `loaded` to `true` so guards can stop showing "loading…" and start
 *    enforcing real checks.
 *  - The merged `permissions` / `sources` fields are computed for you from
 *    `base` and the active project's overrides; callers don't need to merge.
 *  - Safe to call repeatedly — it just overwrites the state.
 *
 * Until a caller invokes this, the store stays in its initial empty state
 * and every `hasPermission` check default-denies (intentional fail-closed).
 */
export function hydratePermissions(payload: PermissionMatrixPayload): void {
  const base = payload.base ?? {};
  const baseSources = payload.baseSources ?? {};
  const projects = payload.projects ?? {};
  const activeProjectId = payload.activeProjectId ?? null;

  const activeOverride = activeProjectId ? projects[activeProjectId] : undefined;
  const mergedPermissions: Record<string, boolean> = {
    ...base,
    ...(activeOverride?.permissions ?? {}),
  };
  const mergedSources: Record<string, PermissionSource> = {
    ...baseSources,
    ...(activeOverride?.sources ?? {}),
  };

  usePermissionsStore.setState({
    loaded: true,
    basePermissions: base,
    baseSources,
    projectPermissions: projects,
    permissions: mergedPermissions,
    sources: mergedSources,
    activeProjectId,
    disabledModules: payload.disabledModules ?? [],
    entitledKeys: payload.entitledKeys ? new Set(payload.entitledKeys) : null,
  });
}

/**
 * Reset the store to its empty/unloaded state. Call on logout so the next
 * user doesn't see the previous user's matrix during the gap before
 * `hydratePermissions` runs again.
 */
export function clearPermissions(): void {
  usePermissionsStore.setState(initialState);
}
